import React, { useState } from 'react';
import { authService } from '../services/authService';
import { AuthUser } from '../lib/supabase';

interface AuthProps {
  onLogin: (user: AuthUser) => void;
}

const Auth: React.FC<AuthProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { user, error } = await authService.signIn(email, password);

      if (error) {
        setError(error);
        setLoading(false);
        return;
      }

      if (user) {
        onLogin(user);
      }
    } catch (err) {
      setError('Ocorreu um erro. Tente novamente.');
    }

    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Digite seu e-mail para recuperar a senha.');
      return;
    }

    setLoading(true);
    const { error } = await authService.resetPassword(email);

    if (error) {
      setError(error);
    } else {
      setError(null);
      alert('E-mail de recuperação enviado! Verifique sua caixa de entrada.');
    }

    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(150deg, #001e42 0%, #003a70 50%, #005bae 100%)' }}
    >
      {/* Decorative neon accent top-right */}
      <div
        className="fixed top-0 right-0 w-96 h-96 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 80% 20%, rgba(163,250,0,0.08) 0%, transparent 60%)',
        }}
      />

      <div className="max-w-md w-full relative">
        {/* Brand header — centered */}
        <div className="mb-10 flex flex-col items-center text-center">
          <div
            className="w-16 h-16 mb-5 rounded-2xl flex items-center justify-center p-1.5"
            style={{ background: '#ffffff', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}
          >
            <img src="/logo.png" alt="GM Gesso Milenio" className="w-full h-full object-contain" />
          </div>
          <h1
            className="text-4xl font-black text-white"
            style={{ letterSpacing: '-0.02em', lineHeight: 1.1 }}
          >
            Gesso Milenio
          </h1>
          <p className="mt-2 font-medium" style={{ color: 'rgba(163,250,0,0.8)' }}>
            Gestão inteligente para profissionais
          </p>
        </div>

        {/* Login card — Glassmorphism */}
        <form
          onSubmit={handleSubmit}
          className="rounded-3xl p-8 space-y-5"
          style={{
            background: 'rgba(255,255,255,0.07)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          }}
        >
          <div className="mb-2">
            <h2 className="text-xl font-bold text-white">Acesse sua conta</h2>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Digite suas credenciais para entrar
            </p>
          </div>

          {error && (
            <div
              className="px-4 py-3 rounded-2xl text-sm font-medium"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.25)' }}
            >
              <i className="fa-solid fa-triangle-exclamation mr-2"></i>
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                E-mail
              </label>
              <div className="relative">
                <i className="fa-solid fa-envelope absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.35)' }}></i>
                <input
                  type="email"
                  required
                  className="w-full pl-11 pr-4 py-3 rounded-xl transition-all font-medium"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: 'white',
                    outline: 'none',
                  }}
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={(e) => { e.target.style.borderColor = '#5fa1ff'; e.target.style.boxShadow = '0 0 0 3px rgba(95,161,255,0.2)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Senha
              </label>
              <div className="relative">
                <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.35)' }}></i>
                <input
                  type="password"
                  required
                  minLength={6}
                  className="w-full pl-11 pr-4 py-3 rounded-xl transition-all font-medium"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: 'white',
                    outline: 'none',
                  }}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={(e) => { e.target.style.borderColor = '#5fa1ff'; e.target.style.boxShadow = '0 0 0 3px rgba(95,161,255,0.2)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>
          </div>

          {/* Primary CTA — gradient */}
          <button
            type="submit"
            disabled={loading}
            className="w-full font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-all"
            style={{
              background: loading ? 'rgba(163,250,0,0.4)' : 'linear-gradient(135deg, #a3fa00 0%, #7dd100 100%)',
              color: '#1f3300',
              boxShadow: loading ? 'none' : '0 8px 20px rgba(163,250,0,0.25)',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: '#1f3300' }}></div>
                Aguarde...
              </>
            ) : (
              <>
                <i className="fa-solid fa-arrow-right-to-bracket"></i>
                Entrar no Painel
              </>
            )}
          </button>

          <div className="text-center pt-1">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm font-medium transition-colors"
              style={{ color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', minHeight: 'auto' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.8)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
            >
              Esqueceu sua senha?
            </button>
          </div>
        </form>

        <div className="mt-8 text-center text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
          Ao entrar você concorda com nossos termos de uso e política de privacidade.
        </div>
      </div>
    </div>
  );
};

export default Auth;
